import { useQuery } from "@apollo/client";
import { GET_TOP_STORIES_BY_CATEGORIES } from "../graphql/stories";
import type { Story, StoryCategory } from "../types/models";

interface CategoryStories {
  category: StoryCategory;
  stories: Story[];
}

interface TopStoriesByCategoriesData {
  topStoriesByCategories: CategoryStories[];
}

interface TopStoriesByCategoriesVariables {
  categories: StoryCategory[];
  limit?: number;
}

// useTopStoriesByCategories hook - Accept array of categories and limit (default limit: 12)
// Returns a map of category to stories for easy lookup
export const useTopStoriesByCategories = (
  categories: StoryCategory[],
  limit = 12
) => {
  const { data, loading, error } = useQuery<
    TopStoriesByCategoriesData,
    TopStoriesByCategoriesVariables
  >(GET_TOP_STORIES_BY_CATEGORIES, {
    variables: { categories, limit },
    // Skip query if categories array is empty
    skip: !categories || categories.length === 0,
  });

  // Transform array response into a map for easy lookup by category
  // Filter out deleted stories as a defensive measure (backend should already filter)
  const categoryStoriesMap = new Map<StoryCategory, Story[]>();
  data?.topStoriesByCategories?.forEach((item) => {
    const filteredStories = item.stories.filter(
      (story) => story.isDeleted !== true
    );
    categoryStoriesMap.set(item.category as StoryCategory, filteredStories);
  });

  return {
    categoryStoriesMap,
    isLoading: loading,
    error,
  };
};

